import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import Header from '../components/common/Header';
import Loading from '../components/common/Loading';

interface Conversa {
  id: string;
  anuncio: {
    id: string;
    titulo: string;
    preco: number;
    fotos: { url: string }[];
  };
  outroUsuario: {
    id: string;
    nome: string;
    avatar?: string;
  };
  ultimaMensagem?: {
    conteudo: string;
    createdAt: string;
    lido: boolean;
  };
  naoLidas: number;
}

export default function Chat() {
  const { usuario } = useAuth();
  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [conversaSelecionada, setConversaSelecionada] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock data - em produção viria da API
    setTimeout(() => {
      setConversas([
        {
          id: '1',
          anuncio: {
            id: 'a1',
            titulo: 'Cavalo Quarto de Milha',
            preco: 25000,
            fotos: [{ url: 'https://picsum.photos/seed/horse1/100/100' }]
          },
          outroUsuario: {
            id: 'u1',
            nome: 'João Silva',
            avatar: 'https://i.pravatar.cc/100?u=joao'
          },
          ultimaMensagem: {
            conteudo: 'Olá, ainda está disponível?',
            createdAt: new Date().toISOString(),
            lido: false
          },
          naoLidas: 2
        },
        {
          id: '2',
          anuncio: {
            id: 'a2',
            titulo: 'Touro Nelore PO',
            preco: 45000,
            fotos: [{ url: 'https://picsum.photos/seed/bull1/100/100' }]
          },
          outroUsuario: {
            id: 'u2',
            nome: 'Maria Santos',
            avatar: 'https://i.pravatar.cc/100?u=maria'
          },
          ultimaMensagem: {
            conteudo: 'Aceita R$ 40.000?',
            createdAt: new Date(Date.now() - 3600000).toISOString(),
            lido: true
          },
          naoLidas: 0
        }
      ]);
      setLoading(false);
    }, 500);
  }, []);

  if (loading) return <Loading />;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          Mensagens
        </h1>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 min-h-[600px]">
            {/* Lista de Conversas */}
            <div className="border-r border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <input
                  type="text"
                  placeholder="Buscar conversas..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              
              <div className="overflow-y-auto max-h-[calc(600px-73px)]">
                {conversas.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p className="text-4xl mb-2">💬</p>
                    <p>Nenhuma conversa ainda</p>
                  </div>
                ) : (
                  conversas.map(conversa => (
                    <button
                      key={conversa.id}
                      onClick={() => setConversaSelecionada(conversa.id)}
                      className={`w-full p-4 flex items-start gap-3 hover:bg-gray-50 transition-colors border-b border-gray-100 ${
                        conversaSelecionada === conversa.id ? 'bg-green-50' : ''
                      }`}
                    >
                      <img
                        src={conversa.outroUsuario.avatar || `https://ui-avatars.com/api/?name=${conversa.outroUsuario.nome}`}
                        alt={conversa.outroUsuario.nome}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex justify-between items-start">
                          <p className="font-semibold text-gray-900 truncate">
                            {conversa.outroUsuario.nome}
                          </p>
                          {conversa.naoLidas > 0 && (
                            <span className="bg-green-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                              {conversa.naoLidas}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                          {conversa.anuncio.titulo}
                        </p>
                        {conversa.ultimaMensagem && (
                          <p className={`text-sm truncate ${
                            conversa.ultimaMensagem.lido ? 'text-gray-400' : 'text-gray-600 font-medium'
                          }`}>
                            {conversa.ultimaMensagem.conteudo}
                          </p>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Área do Chat */}
            <div className="col-span-2">
              {conversaSelecionada ? (
                <div className="h-full flex flex-col">
                  {/* Header */}
                  <div className="p-4 border-b border-gray-200 bg-gray-50">
                    <p className="font-semibold">{conversas.find(c => c.id === conversaSelecionada)?.outroUsuario.nome}</p>
                    <p className="text-sm text-gray-500">{conversas.find(c => c.id === conversaSelecionada)?.anuncio.titulo}</p>
                  </div>
                  {/* Messages */}
                  <div className="flex-1 p-4 overflow-y-auto">
                    <p className="text-center text-gray-400 text-sm">Chat em desenvolvimento</p>
                  </div>
                  {/* Input */}
                  <div className="p-4 border-t border-gray-200">
                    <input type="text" placeholder="Digite sua mensagem..." className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <p className="text-6xl mb-4">💬</p>
                    <p className="text-lg">Selecione uma conversa para começar</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
